import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Box,
	Button,
	Checkbox,
	Collapse,
	Divider,
	FormControl,
	Radio,
	RadioGroup,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tooltip,
	Tr,
	useDisclosure,
	useToast,
	VStack,
	Wrap,
	WrapItem,
	Input,
} from "@chakra-ui/react";
import moment from "moment";
import { CgCalculator } from "react-icons/cg";
import {
	FiCalendar,
	FiCheckCircle,
	FiCreditCard,
	FiEdit3,
	FiFileText,
	FiGlobe,
	FiHash,
	FiInfo,
	FiPlus,
	FiSettings,
	FiTrendingUp,
	FiUpload,
	FiUser,
} from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";


import SearchBox from "../../Pages/Properties/SearchBox";
import { addAdministrationFeePaymentAsync, addPaymentAsync, addRentPaymentAsync, updatePropertyCustomOwnerPlanAsync } from "../../redux/user/userSlice";
import { addAdmFeePaymentVerifier, addPaymentVerifier, getPropertyAddress, getPropertyStatus } from "../../utils/verifier";
import Dropzone from "../Dropzone/Dropzone";
import InvoicesRow from "../TableRows/InvoicesRow";

function _arrayBufferToBase64(buffer) {
	var binary = "";
	var bytes = new Uint8Array(buffer);
	var len = bytes.byteLength;
	for (var i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary);
}
const paymentTypes = [
	{ id: "tax", label: "Impozit" },
	{ id: "centralHeatingVerification", label: "Verificare centrala" },
	{ id: "insurance", label: "Asigurare" },
	// { id: "rent", label: "Chirie" },
	{ id: "other", label: "Altele" },
];
export default function PaymentsTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;

	const initialPaymentInformation = {
		paymentDate: moment().format("yyyy-MM-DD"),
		paymentDirection: "incoming",
		paymentMethod: "op",
		paymentNumber: "",
		paymentType: "",
		propertyId: property.id,
		amount: "",
		currency: "ron",
		documents: [],
		billedTo: {},
		owner: property.owner.name,
		equivalentAmount: "",
		equivalentCurrency: "",
		description: "",
		invoiceNumber: "",
	};
	const [query, setquery] = useState("");
	const handleSearch = (query) => {
		setquery(query);
	};
	const payments = property.payments
		.filter((payment) => !payment.type.toLowerCase().includes("utility"))
		.filter((payment) => {
			return (
				payment.type?.toLowerCase().includes(query.toLowerCase()) ||
				payment.amount?.toLowerCase().includes(query.toLowerCase()) ||
				payment.paymentMethod?.toLowerCase().includes(query.toLowerCase())
			);
		});

	const dispatch = useDispatch();
	const ownerBillingInformation = property.owner.billingInformation;
	const user = useSelector((state) => state.user.user);
	const loading = useSelector((state) => state.user.status) === "loading";
	const agencyBillingInformation = user.billingInformation;
	const customOwnerPlans = user.customOwnerPlans || [];
	const [currentCustomOwnerPlans, setCurrentCustomOwnerPlans] = useState(property.customOwnerPlans || []);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isAddRentOpen, onOpen: onAddRentOpen, onClose: onAddRentClose } = useDisclosure();
	const { isOpen: isAddAdmFeeOpen, onOpen: onAddAdmFeeOpen, onClose: onAddAdmFeeClose } = useDisclosure();
	const { isOpen: isEditOwnerPlanListOpen, onOpen: onEditOwnerPlanListOpen, onClose: onEditOwnerPlanListClose } = useDisclosure();
	const [paymentInformation, setPaymentInformation] = useState(initialPaymentInformation);
	const { isOpen: isCustomAdmFeeAmountOpen, onToggle: onCustomAdmFeeAmountToggle, onClose: onCustomAdmFeeAmountClose } = useDisclosure();
	const { isOpen: isCustomRentAmountOpen, onToggle: onCustomRentAmountToggle, onClose: onCustomRentAmountClose } = useDisclosure();
	const [error, setError] = useState({});
	const toast = useToast();
	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		let documents = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			documents.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}
		setPaymentInformation({ ...paymentInformation, documents });
	};
	const initialAdmFeeInfo = { amount: 0, currency: "eur", alreadyPaid: true, customOwnerPlans: currentCustomOwnerPlans };
	const [admFeeInfo, setAdmFeeInfo] = useState(initialAdmFeeInfo);
	const [admFeeAmountType, setAdmFeeAmountType] = useState("current");
	const initialRentInfo = { amount: 0, currency: "" };
	const [rentInfo, setRentInfo] = useState(initialRentInfo);
	const [rentAmountType, setRentAmountType] = useState("current");
	const handleSaveEditOwnerPlanList = () => {
		const payload = {
			propertyId: property.id,
			customOwnerPlans: currentCustomOwnerPlans,
		};
		console.log(payload);
		dispatch(updatePropertyCustomOwnerPlanAsync(payload))
			.then((r) => {
				if (r.error?.message) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success!",
						description: "Saved!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onEditOwnerPlanListClose();
			});
	};
	const addRentPayment = () => {
		let payload;

		if (rentAmountType === "current") {
			// Use current rent amount from property
			payload = {
				amount: property.rent.amount,
				currency: property.rent.currency,
				propertyId: property.id,
			};
		} else {
			// Use custom amount
			const errors = addAdmFeePaymentVerifier(rentInfo);
			setError(errors);
			console.log(errors);

			payload = { ...rentInfo, propertyId: property.id };
			//if there are errors show a toast and return
			if (Object.entries(errors).length !== 0) {
				toast({
					title: "Error encountered.",
					description: "Invalid information",
					status: "error",
					duration: 2000,
					isClosable: true,
				});
				return;
			}
		}

		console.log(payload);
		dispatch(addRentPaymentAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Payment added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onAddRentClose();
				if (isCustomRentAmountOpen) {
					setRentInfo(initialRentInfo);
				}
				onCustomRentAmountClose();
				setRentAmountType("current");
			});
	};
	const addAdmFeePayment = async () => {
		console.log(admFeeInfo);
		if (admFeeAmountType === "custom") {
			const errors = addAdmFeePaymentVerifier(admFeeInfo);
			setError(errors);
			console.log(errors);
			if (Object.entries(errors).length !== 0) {
				toast({
					title: "Error encountered.",
					description: "Invalid information",
					status: "error",
					duration: 2000,
					isClosable: true,
				});
				return;
			}
		}
		const payload = { ...admFeeInfo, propertyId: property.id };
		console.log(payload);
		dispatch(addAdministrationFeePaymentAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Payment added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onAddAdmFeeClose();
				if (isCustomAdmFeeAmountOpen) {
					setAdmFeeInfo(initialAdmFeeInfo);
				}
				onCustomAdmFeeAmountClose();
				setAdmFeeAmountType("current");
			});
	};
	const addPayment = () => {
		const errors = addPaymentVerifier(paymentInformation);
		const admCtr = property.documents.find((d) => d.type === "administrationContract" && d.active);
		const leaseCtr = property.documents.find((d) => d.type === "lease" && d.active) || {};
		const payload = {
			...paymentInformation,
			administrationContractDate: admCtr.startingDate,
			administrationContractNumber: admCtr.number,
			leaseNumber: leaseCtr.number,
			leaseDate: leaseCtr.signatureDate,
		};
		console.log(payload);
		if (Object.entries(errors).length !== 0) {
			console.log(errors);
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addPaymentAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Payment added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onClose();
				setPaymentInformation(initialPaymentInformation);
			});
	};
	const handleChange = (e) => {
		delete error[e.target.name];
		setPaymentInformation({ ...paymentInformation, [e.target.name]: e.target.value });
	};
	const handleAdmFeeChange = (e) => {
		setAdmFeeInfo({ ...admFeeInfo, [e.target.name]: e.target.value });
	};
	const handleRentChange = (e) => {
		setRentInfo({ ...rentInfo, [e.target.name]: e.target.value });
	};
	const handleBilledToChange = (e) => {
		const billedToEmail = e.target.value;
		console.log("email: ", billedToEmail);

		if (property.owner.email === billedToEmail) {
			setPaymentInformation({ ...paymentInformation, billedTo: { name: property.owner.name, email: billedToEmail, type: "owner" } });
			return;
		}
		if (property.contacts.find((c) => c.email === billedToEmail)) {
			const contact = property.contacts.find((c) => c.email === billedToEmail);
			console.log(contact);
			setPaymentInformation({ ...paymentInformation, billedTo: { name: contact.name, email: billedToEmail, type: contact.contactType } });
			return;
		}
		if (property.leaseContacts.find((c) => c.id === billedToEmail)) {
			const contact = property.leaseContacts.find((c) => c.id === billedToEmail);
			console.log(contact);
			setPaymentInformation({ ...paymentInformation, billedTo: { name: contact.name, email: billedToEmail, type: contact.contactType } });
			return;
		}
		if (property.tenants.find((c) => c.email === billedToEmail)) {
			const tenant = property.tenants.find((c) => c.email === billedToEmail);
			console.log(tenant);
			setPaymentInformation({ ...paymentInformation, billedTo: { name: tenant.name, email: billedToEmail, type: "tenant" } });
			return;
		}
	};

	const renderAddPaymentModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="4xl"
			icon={FiPlus}
			title={t("property.addPayment")}
			subtitle={t("property.addPaymentSubtitle")}
			footer={
				<ModalFooterActions
					isLoading={loading}
					onCancel={() => {
						onClose();
						setPaymentInformation(initialPaymentInformation);
						onCustomAdmFeeAmountClose();
						setError({});
					}}
					onConfirm={addPayment}
					confirmLabel={t("misc.save")}
				/>
			}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.paymentInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("property.paymentDate")}</ModalFieldLabel>
									<Input size="sm" type="date" name="paymentDate" value={paymentInformation.paymentDate} onChange={handleChange} borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiFileText}>{t("property.paymentDirection")}</ModalFieldLabel>
									<Select size="sm" onChange={handleChange} isInvalid={error?.paymentDirection} name="paymentDirection" value={paymentInformation.paymentDirection} borderRadius="md">
										<option value="incoming">Incasare</option>
										<option value="outgoing">Plata</option>
									</Select>
								</FormControl>
							</SimpleGrid>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiFileText}>{t("property.documentNumber")}</ModalFieldLabel>
									<Input size="sm" onChange={handleChange} isInvalid={error?.paymentNumber} type="text" name="paymentNumber" placeholder={t("property.documentNumber")} borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiCreditCard}>{t("property.method")}</ModalFieldLabel>
									<Select size="sm" isInvalid={error?.paymentMethod} onChange={handleChange} name="paymentMethod" borderRadius="md">
										<option value="op">OP</option>
										<option value="cash">Cash</option>
									</Select>
								</FormControl>
							</SimpleGrid>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
									<Input size="sm" isInvalid={error?.amount} type="number" name="amount" placeholder={t("property.amount")} value={paymentInformation.amount} onChange={handleChange} borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
									<Select size="sm" name="currency" isInvalid={error?.currency} value={paymentInformation.currency} onChange={handleChange} borderRadius="md">
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.type")}</ModalFieldLabel>
								<Select size="sm" onChange={handleChange} isInvalid={error?.paymentType} name="paymentType" placeholder={t("property.type")} borderRadius="md">
									{paymentTypes.map((d, index) => (
										<option key={index} value={d.id}>{d.label}</option>
									))}
								</Select>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiHash}>{t("property.invoiceNumber")}</ModalFieldLabel>
								<Input
									size="sm"
									onChange={handleChange}
									type="text"
									name="invoiceNumber"
									placeholder={t("property.invoiceNumberPlaceholder")}
									value={paymentInformation.invoiceNumber}
									borderRadius="md"
								/>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.description")}</ModalFieldLabel>
								<Textarea size="sm" name="description" placeholder={t("property.description")} isInvalid={error?.description} value={paymentInformation.description} onChange={handleChange} rows={2} borderRadius="md" />
							</FormControl>
						</VStack>
					</PropertyModalSection>
				</VStack>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiUser} title={t("property.billingDetails")}>
						<VStack spacing={3} align="stretch">
							<FormControl>
								<ModalFieldLabel icon={FiUser}>{t("property.billedTo")}</ModalFieldLabel>
								<Select size="sm" onChange={handleBilledToChange} isInvalid={error?.billedTo} name="billedTo" placeholder={t("property.billedTo")} borderRadius="md">
									<option value={property.owner.email}>{property.owner.name}</option>
									{property.tenants?.map((c, index) => (<option key={index} value={c.email}>{c.name}</option>))}
									{property.contacts?.map((c, index) => (<option key={index} value={c.email}>{c.name}</option>))}
									{property.leaseContacts?.map((c, index) => (<option key={index} value={c.id}>{c.name}</option>))}
								</Select>
							</FormControl>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiUser}>{t("property.billedToName")}</ModalFieldLabel>
									<Input size="sm" isInvalid={error?.billedToName} type="text" name="name" placeholder={t("property.billedToName")} value={paymentInformation.billedTo.name} onChange={(e) => { setPaymentInformation({ ...paymentInformation, billedTo: { ...paymentInformation.billedTo, name: e.target.value } }); }} borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiFileText}>{t("property.billedToType")}</ModalFieldLabel>
									<Input size="sm" isInvalid={error?.billedToType} type="text" name="type" placeholder={t("property.billedToType")} value={paymentInformation.billedTo.type} onChange={(e) => { setPaymentInformation({ ...paymentInformation, billedTo: { ...paymentInformation.billedTo, type: e.target.value } }); }} borderRadius="md" />
								</FormControl>
							</SimpleGrid>
						</VStack>
					</PropertyModalSection>
					<PropertyModalSection icon={FiUpload} title={t("property.documents")}>
						<Dropzone onDrop={onDocumentDrop} accept={{ "application/pdf": [".pdf"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], "application/msword": [".doc"] }} description={t("property.uploadPdfDocDocx")} />
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	const renderAddRentModal = () => (
		<PropertyModalShell
			isOpen={isAddRentOpen}
			onClose={onAddRentClose}
			size="3xl"
			icon={FiTrendingUp}
			title={t("property.addRentPayment")}
			subtitle={t("property.addRentPaymentSubtitle")}
			footer={
				<ModalFooterActions
					isLoading={loading}
					onCancel={() => { onAddRentClose(); setRentInfo(initialRentInfo); setRentAmountType("current"); }}
					onConfirm={addRentPayment}
					confirmLabel={t("property.confirm")}
				/>
			}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<Alert status="info" borderRadius="md" size="sm">
					<AlertIcon />
					<AlertDescription fontSize="sm">{t("property.rentPaymentDisclaimer")}</AlertDescription>
				</Alert>
				<PropertyModalSection icon={CgCalculator} title={t("property.paymentAmount")}>
					<RadioGroup value={rentAmountType} onChange={setRentAmountType}>
						<VStack spacing={3} align="start">
							<Radio value="current">
								<VStack align="start" spacing={0}>
									<Text fontSize="sm" fontWeight="medium">{t("property.currentRentAmount")}</Text>
									<Text fontSize="xs" color="gray.600">{property.rent.amount} {property.rent.currency}</Text>
								</VStack>
							</Radio>
							<Radio value="custom">
								<VStack align="start" spacing={0}>
									<Text fontSize="sm" fontWeight="medium">{t("property.customAmount")}</Text>
									<Text fontSize="xs" color="gray.600">{t("property.enterDifferentAmount")}</Text>
								</VStack>
							</Radio>
						</VStack>
						<Collapse in={rentAmountType === "custom"} animateOpacity>
							<SimpleGrid columns={2} spacing={4} w="full" mt={3}>
								<FormControl>
									<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
									<Input size="sm" isInvalid={error?.amount} type="number" name="amount" placeholder={t("property.amount")} value={rentInfo?.amount} onChange={handleRentChange} borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
									<Select size="sm" name="currency" isInvalid={error?.currency} value={rentInfo?.currency} onChange={handleRentChange} borderRadius="md">
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
								</FormControl>
							</SimpleGrid>
						</Collapse>
					</RadioGroup>
				</PropertyModalSection>
			</VStack>
		</PropertyModalShell>
	);

	const renderAddAdmFeeModal = () => (
		<PropertyModalShell
			isOpen={isAddAdmFeeOpen}
			onClose={onAddAdmFeeClose}
			size="3xl"
			icon={FiSettings}
			title={t("property.addAdministrationFeePayment")}
			subtitle={t("property.addAdmFeePaymentSubtitle")}
			footer={
				<ModalFooterActions
					isLoading={loading}
					onCancel={() => { onAddAdmFeeClose(); setAdmFeeInfo(initialAdmFeeInfo); setAdmFeeAmountType("current"); }}
					onConfirm={addAdmFeePayment}
					confirmLabel={t("property.confirm")}
				/>
			}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<Alert status="info" borderRadius="md" size="sm">
					<AlertIcon />
					<AlertDescription fontSize="sm">{t("property.admFeePaymentDescription")}</AlertDescription>
				</Alert>
				<PropertyModalSection icon={CgCalculator} title={t("property.feeConfiguration")}>
					<Checkbox hidden={true} onChange={(e) => { setAdmFeeInfo({ ...admFeeInfo, alreadyPaid: e.target.checked }); }} isChecked={admFeeInfo.alreadyPaid}>{t("property.alreadyPaid")}</Checkbox>
					<RadioGroup value={admFeeAmountType} onChange={setAdmFeeAmountType}>
						<VStack spacing={3} align="start" mt={2}>
							<Radio value="current">
								<VStack align="start" spacing={0}>
									<Text fontSize="sm" fontWeight="medium">{t("property.currentAdministrationFee")}</Text>
									<Text fontSize="xs" color="gray.600">
										{property.rent.fixedAdministrationFeeAmount ? `${property.rent.fixedAdministrationFeeAmount} ${property.rent.fixedAdministrationFeeCurrency}` : `${(property.rent.amount * property.owner.bankDetails.administrationFeePercent) / 100} ${property.rent.currency}`}
									</Text>
								</VStack>
							</Radio>
							<Radio value="custom">
								<VStack align="start" spacing={0}>
									<Text fontSize="sm" fontWeight="medium">{t("property.customAmount")}</Text>
									<Text fontSize="xs" color="gray.600">{t("property.enterDifferentAmount")}</Text>
								</VStack>
							</Radio>
						</VStack>
						<Collapse in={admFeeAmountType === "custom"} animateOpacity>
							<SimpleGrid columns={2} spacing={4} w="full" mt={3}>
								<FormControl>
									<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
									<Input size="sm" isInvalid={error?.amount} type="number" name="amount" placeholder={t("property.amount")} value={admFeeInfo?.amount} onChange={handleAdmFeeChange} borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
									<Select size="sm" name="currency" isInvalid={error?.currency} value={admFeeInfo?.currency} onChange={handleAdmFeeChange} borderRadius="md">
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
								</FormControl>
							</SimpleGrid>
						</Collapse>
					</RadioGroup>
				</PropertyModalSection>
			</VStack>
		</PropertyModalShell>
	);

	const renderEditCustomOwnerPlanListModal = () => (
		<PropertyModalShell
			isOpen={isEditOwnerPlanListOpen}
			onClose={onEditOwnerPlanListClose}
			size="5xl"
			icon={FiEdit3}
			title={t("property.editMonthlyServices")}
			subtitle={t("property.editMonthlyServicesSubtitle")}
			footer={
				<ModalFooterActions onCancel={onEditOwnerPlanListClose} onConfirm={handleSaveEditOwnerPlanList} confirmLabel={t("misc.save")} />
			}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<Alert status="info" borderRadius="md" size="sm">
					<AlertIcon />
					<AlertDescription fontSize="sm">{t("property.monthlyServicesDescription")}</AlertDescription>
				</Alert>
				<PropertyModalSection icon={CgCalculator} title={t("property.estimatedTotal")}>
					<SimpleGrid columns={2} spacing={4}>
						<Box>
							<Text fontSize="xs" color="gray.500" mb={1}>RON</Text>
							<Text fontSize="md" fontWeight="bold" color="green.500">{customOwnerPlans.reduce((acc, plan) => { if (currentCustomOwnerPlans.includes(plan.id) && plan.price.currency === "ron") return acc + Number(plan.price.amount); return acc; }, 0)}</Text>
						</Box>
						<Box>
							<Text fontSize="xs" color="gray.500" mb={1}>EUR</Text>
							<Text fontSize="md" fontWeight="bold" color="blue.500">{customOwnerPlans.reduce((acc, plan) => { if (currentCustomOwnerPlans.includes(plan.id) && plan.price.currency === "eur") return acc + Number(plan.price.amount); return acc; }, 0)}</Text>
						</Box>
					</SimpleGrid>
					<Text fontSize="xs" color="gray.500" mt={2}>{t("property.perMonth")}</Text>
				</PropertyModalSection>
				<PropertyModalSection icon={FiSettings} title={t("property.availableServices")}>
					<Box overflowX="auto">
						<Table size="sm" variant="simple">
							<Thead>
								<Tr><Th></Th><Th>{t("table.name")}</Th><Th>{t("table.description")}</Th><Th>{t("table.price")}</Th></Tr>
							</Thead>
							<Tbody>
								{customOwnerPlans.map((plan, index) => (
									<Tr key={index}>
										<Td><Checkbox isChecked={currentCustomOwnerPlans?.some((p) => p === plan.id)} onChange={(e) => { if (e.target.checked) setCurrentCustomOwnerPlans(currentCustomOwnerPlans.concat(plan.id)); else setCurrentCustomOwnerPlans(currentCustomOwnerPlans.filter((p) => p !== plan.id)); }} /></Td>
										<Td whiteSpace="nowrap">{plan.name}</Td>
										<Td>{plan.description}</Td>
										<Td>{plan.price.amount} {plan.price.currency}</Td>
									</Tr>
								))}
								{customOwnerPlans.length === 0 && (<Tr><Td colSpan={4} textAlign="center">{t("table.noData")}</Td></Tr>)}
							</Tbody>
						</Table>
					</Box>
				</PropertyModalSection>
			</VStack>
		</PropertyModalShell>
	);

	return (
		<Stack>
			<Wrap>
				<WrapItem>
					<SearchBox
						handleSearch={handleSearch}
						searchOptions={[
							{
								label: "Type",
								description: "Search by type",
							},
							{
								label: "Amount",
								description: "Search by amount",
							},
							{
								label: "Method",
								description: "Search by method",
							},
						]}
					/>
				</WrapItem>
				<WrapItem>
					<Tooltip
						isDisabled={
							getPropertyStatus(property) === "inactive" ||
							Object.keys(ownerBillingInformation).length !== 0 ||
							Object.keys(agencyBillingInformation).length !== 0
						}
						label={
							getPropertyStatus(property) === "inactive" ? "Add contracts to enable" : "You need to add billing information for owner and agency to enable"
						}
					>
						<Button
							onClick={onOpen}
							colorScheme="blue"
							isDisabled={
								getPropertyStatus(property) === "inactive" ||
								Object.keys(ownerBillingInformation).length === 0 ||
								Object.keys(agencyBillingInformation).length === 0 ||
								isArchived
							}
							width="fit-content"
						>
							{t("property.addPayment")}
						</Button>
					</Tooltip>
				</WrapItem>
				<WrapItem>
					<Tooltip
						isDisabled={
							getPropertyStatus(property) === "inactive" ||
							Object.keys(ownerBillingInformation).length !== 0 ||
							Object.keys(agencyBillingInformation).length !== 0
						}
						label={
							getPropertyStatus(property) === "inactive" ? "Add contracts to enable" : "You need to add billing information for owner and agency to enable"
						}
					>
						<Button
							onClick={onAddRentOpen}
							colorScheme="green"
							isDisabled={
								getPropertyStatus(property) === "inactive" ||
								Object.keys(ownerBillingInformation).length === 0 ||
								Object.keys(agencyBillingInformation).length === 0 ||
								isArchived
							}
							width="fit-content"
						>
							{t("property.addRentPayment")}
						</Button>
					</Tooltip>
				</WrapItem>
				<WrapItem>
					<Tooltip
						isDisabled={Object.keys(ownerBillingInformation).length !== 0 || Object.keys(agencyBillingInformation).length !== 0}
						label={"You need to add billing information for owner and agency to enable"}
					>
						<Button onClick={onAddAdmFeeOpen} colorScheme="blue" isDisabled={isArchived}>
							{t("property.addAdministrationFeePayment")}
						</Button>
					</Tooltip>
				</WrapItem>
				<WrapItem>
					<Tooltip
						isDisabled={Object.keys(ownerBillingInformation).length !== 0 || Object.keys(agencyBillingInformation).length !== 0}
						label={"You need to add billing information for owner and agency to enable"}
					>
						<Button onClick={onEditOwnerPlanListOpen} colorScheme="blue" isDisabled={isArchived}>
							{t("property.editCustomOwnerPlanList")}
						</Button>
					</Tooltip>
				</WrapItem>
			</Wrap>

			<Divider />
			{renderAddPaymentModal()}
			{renderAddRentModal()}
			{renderAddAdmFeeModal()}
			{renderEditCustomOwnerPlanListModal()}
			<Box overflowX="auto">
				<Table variant="simple" size="small">
					<Thead fontWeight={"bold"}>
						<Tr pl="0px">
							<Th pl="0px">{t("table.issuedDate")}</Th>
							<Th>{t("table.status")}</Th>
							<Th>{t("table.dueDate")}</Th>
							<Th>{t("table.amount")}</Th>
							<Th>{t("table.method")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.paidDate")}</Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{payments
							.filter((p) => !p.paymentDate)
							.map((row, index) => {
								return (
									<InvoicesRow
										id={row.id}
										key={index}
										date={row.issuedDate}
										address={getPropertyAddress(property)}
										paymentDate={row.paymentDate}
										code={row.number}
										amount={row.amount}
										type={row.type}
										dueDate={row.dueDate}
										logo={row.logo}
										format={row.format}
										method={row.paymentMethod}
										documents={row.documents}
										paymentDirection={row.paymentDirection}
										property={property}
										currency={row.currency}
										description={row.description}
										exchange={row.exchange}
										invoiceNumber={row.invoiceNumber}
									/>
								);
							})}
						{payments
							.filter((p) => p.paymentDate)
							.map((row, index) => {
								return (
									<InvoicesRow
										id={row.id}
										key={index}
										date={row.issuedDate}
										address={getPropertyAddress(property)}
										paymentDate={row.paymentDate}
										code={row.number}
										amount={row.amount}
										type={row.type}
										dueDate={row.dueDate}
										logo={row.logo}
										format={row.format}
										method={row.paymentMethod}
										documents={row.documents}
										paymentDirection={row.paymentDirection}
										property={property}
										currency={row.currency}
										invoiceNumber={row.invoiceNumber}
										description={row.description}
										exchange={row.exchange}
									/>
								);
							})}
					</Tbody>
				</Table>
			</Box>
		</Stack>
	);
}
